import { useState, useEffect, useMemo } from "react";
import styled from "styled-components";
import { Slider } from "@mui/material";
import { breakpoint, theme } from "@utilities";
import { CustomCheckbox } from "@components";

interface CommissionSliderProps {
    maxCommissionDollar: number;
    pointsMultiplier: number;
    value?: number;
    onChange: (value: number) => void;
    onMaxReached?: (isMax: boolean) => void;
    isLoading?: boolean;
    onCheckboxChange?: () => void;
    isChecked?: boolean;
    isCheckboxDisabled?: boolean;
}

export const CommissionSlider: React.FC<CommissionSliderProps> = ({
    maxCommissionDollar,
    pointsMultiplier,
    value: externalValue,
    onChange,
    onMaxReached,
    isLoading = false,
    onCheckboxChange,
    isChecked = false,
    isCheckboxDisabled = false,
}) => {
    const [value, setValue] = useState(maxCommissionDollar / 2);

    const isAtMax = value >= maxCommissionDollar;

    const handleSliderChange = (_: any, newValue: number | number[]) => {
        const val = Array.isArray(newValue) ? newValue[0] : newValue;
        setValue(val);
        onChange(Math.round((maxCommissionDollar - val) * pointsMultiplier * 100) / 100);
        onMaxReached?.(val >= maxCommissionDollar);
    };

    useEffect(() => {
        if (!isChecked) {
            setValue(maxCommissionDollar / 2);
            onChange(Math.round((maxCommissionDollar / 2) * pointsMultiplier * 100) / 100);
            onMaxReached?.(false);
        }
    }, [isChecked, maxCommissionDollar, pointsMultiplier, onChange, onMaxReached]);

    useEffect(() => {
        setValue(maxCommissionDollar / 2);
        onChange(Math.round((maxCommissionDollar / 2) * pointsMultiplier * 100) / 100);
        onMaxReached?.(false);
    }, [maxCommissionDollar]);

    useEffect(() => {
        if (externalValue !== undefined) {
            const sliderValue = maxCommissionDollar - externalValue / pointsMultiplier;
            setValue(sliderValue);
            onMaxReached?.(sliderValue >= maxCommissionDollar);
        }
    }, [externalValue, maxCommissionDollar, pointsMultiplier, onMaxReached]);

    const sliderStyles = useMemo(
        () => ({
            width: "100%",
            height: 8,
            "& .MuiSlider-rail, & .MuiSlider-track": {
                height: 10,
                borderRadius: 6,
                border: "none",
                "&::before, &::after": {
                    content: '""',
                    position: "absolute",
                    width: 6,
                    height: 6,
                    backgroundColor: "white",
                    borderRadius: "50%",
                    top: "50%",
                    transform: "translateY(-50%)",
                    zIndex: 1,
                },
                "&::before": { left: 2 },
                "&::after": { right: 2 },
            },
            "& .MuiSlider-rail": {
                background: `${theme.colors.highlight}`,
                opacity: 1,
            },
            "& .MuiSlider-track": {
                background: isAtMax
                    ? "#FF9152"
                    : `linear-gradient(90deg,${theme.colors.main} 99.99%, #7257FF 100%)`,
            },
            "& .MuiSlider-thumb": {
                width: 20,
                height: 20,
                backgroundColor: isAtMax ? "#FF9152" : theme.colors.main,
                "&:hover, &.Mui-focusVisible": { boxShadow: "none" },
            },
            "& .MuiSlider-mark": { display: "none" },
            "@media (pointer: coarse)": { padding: "0 !important" },
        }),
        [isAtMax],
    );

    return (
        <SliderWrapper>
            <CheckboxRow>
                {isLoading ? (
                    <LoadingSpinner />
                ) : (
                    <CustomCheckbox
                        isChecked={isChecked}
                        onClick={onCheckboxChange}
                        disabled={isCheckboxDisabled}
                    />
                )}
                <CheckboxLabel>Adjust the Sell Price</CheckboxLabel>
            </CheckboxRow>

            {!isChecked && <PriceSection />}

            {isChecked && (
                <>
                    <HintText>Slide to reduce or increase your commission.</HintText>
                    <div className="slider-container">
                        <Slider
                            value={value}
                            onChange={handleSliderChange}
                            min={0}
                            max={maxCommissionDollar}
                            step={1}
                            sx={sliderStyles}
                        />
                    </div>

                    <CommissionSliderLabels>
                        <span>
                            <span className="number">0</span>Decrease
                        </span>
                        <span>|</span>
                        <span>
                            Increase<span className="number">{maxCommissionDollar.toFixed(2)}</span>
                        </span>
                    </CommissionSliderLabels>
                </>
            )}
        </SliderWrapper>
    );
};

const SliderWrapper = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;

    .slider-container {
        ${breakpoint.breakOnlyMobile`
            padding: 0 8px;
        `}
    }
`;

const CheckboxRow = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
`;

const LoadingSpinner = styled.div`
    width: 20px;
    height: 20px;
    border: 2px solid #e6e9eb;
    border-top: 2px solid ${theme.colors.highlight};
    border-radius: 50%;
    animation: spin 1s linear infinite;

    @keyframes spin {
        0% {
            transform: rotate(0deg);
        }
        100% {
            transform: rotate(360deg);
        }
    }
`;

const CheckboxLabel = styled.span`
    font-weight: 500;
    font-size: 14px;
    color: #131214;
`;

const HintText = styled.span`
    font-weight: 400;
    font-size: 12px;
    line-height: 120%;
    color: #6e7375;
    margin: 12px 0 12px 0;
`;

const CommissionSliderLabels = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 12px;
    position: relative;

    span {
        font-family: "Poppins";
        font-size: 10px;
        line-height: 120%;
        color: #898d8f;

        &:nth-child(1) {
            text-align: left;
            .number {
                margin-right: 24px;
            }
        }

        &:nth-child(2) {
            position: absolute;
            left: 50%;
            transform: translateX(-50%);
        }

        &:nth-child(3) {
            text-align: right;
            .number {
                margin-left: 24px;
            }
        }
    }
`;

const PriceSection = styled.div``;
